import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { QueryParamParser } from '../query-params.ts';

type SetValue<Value> = (value: Value | undefined | null, action?: 'replace' | 'push') => void;
type Result<T, D = undefined> = [T | D, SetValue<T>];

export function useQueryParam<V>(key: string, parser: QueryParamParser<V>): Result<V>;
export function useQueryParam<V>(key: string, parser: QueryParamParser<V>, defaultValue: V): Result<V, V>;

export function useQueryParam<TValue>(
    name: string,
    parser: QueryParamParser<TValue>,
    defaultValue?: TValue,
): Result<TValue> {
    const [params, setParams] = useSearchParams();
    const paramsRef = useRef(params);

    useLayoutEffect(() => {
        paramsRef.current = params;
    }, [params]);

    const queryValue = params.get(name);
    const value = useMemo(() => (queryValue ? parser.decode(queryValue) : undefined), [queryValue, parser]);

    const setValue = useCallback<SetValue<TValue>>(
        (nextValue: TValue | undefined | null, action = 'replace') => {
            const nextParams = new URLSearchParams(paramsRef.current);

            if (nextValue === undefined || nextValue === null) {
                nextParams.delete(name);
            } else {
                nextParams.set(name, parser.encode(nextValue));
            }

            setParams(nextParams, { replace: action === 'replace' });
        },
        [name, parser, setParams],
    );

    return [value ?? defaultValue, setValue];
}
